import {
  dataErrorKey,
  dataErrorMessageKey,
  isUnreachableError,
} from "@/components/atomic-crm/providers/commons/dataError";

/** The bits of a react-query / ra-core list result the load gate needs. */
export type QuerySnapshot = {
  data?: unknown;
  error?: unknown;
  isPaused?: boolean;
  isPending?: boolean;
  refetch?: () => unknown;
};

export type QueryLoadPhase = "ready" | "loading" | "offline" | "error";

const isUnreachable = (query: QuerySnapshot) =>
  query.isPaused === true || isUnreachableError(query.error);

/**
 * A list is ready only when it has a payload, including a successful empty
 * array or data kept from the previous success. `data === undefined` means
 * the fetch has not produced a result.
 */
export const queryLoadPhase = (
  queries: QuerySnapshot[],
  browserOffline = false,
): QueryLoadPhase => {
  const unresolved = queries.filter((query) => query.data === undefined);
  if (unresolved.length === 0) return "ready";
  // A reload that starts offline pauses or never settles the fetch. That is
  // not a spinner, and it is not an empty list.
  if (browserOffline) return "offline";
  const failed = unresolved.filter(
    (query) => query.isPaused === true || query.error != null,
  );
  if (failed.length === 0) return "loading";
  return failed.some(isUnreachable) ? "offline" : "error";
};

/**
 * Cached rows stay on screen, but the user needs to see that a refresh did
 * not happen. A paused query or a failed fetch counts even if `navigator`
 * has not flipped yet.
 */
export const queryShowsStaleOffline = (
  queries: QuerySnapshot[],
  browserOffline: boolean,
) =>
  queries.length > 0 &&
  queries.every((query) => query.data !== undefined) &&
  (browserOffline || queries.some(isUnreachable));

/** Load failures never use the save-failure sentence (`crm.errors.network`). */
export const queryLoadMessageKey = (
  queries: QuerySnapshot[],
  browserOffline = false,
) => {
  if (browserOffline || queries.some(isUnreachable))
    return "crm.errors.offline";
  const error = queries.find((query) => query.error != null)?.error;
  const key = dataErrorMessageKey(error);
  return key === dataErrorKey.network ? "crm.errors.offline" : key;
};
